import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api, { API_ENDPOINT } from '@/api';
import { NoticeBox } from '@/common/Notice';
import { CertificateNotice } from '@/courses/CertificateNotice';
import { CourseContent } from '@/courses/CourseContent';
import { CourseHero } from '@/courses/CourseHero';
import { CourseInfoSidebar } from '@/courses/CourseInfoSidebar';
import { CourseProgressIndicator } from '@/courses/CourseProgressIndicator';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { formatString } from '~/utils/stringFormat';

export const Route = createFileRoute('/_app/courses/$courseSlug')({
  component: CourseScreen,
  beforeLoad: () => {
    return {
      noContainer: true,
      noOverflowHidden: true,
    };
  },
});

function CourseScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<
    Record<number, boolean>
  >({});
  const { me, isStudent } = useCurrentUser();
  const { courseSlug } = Route.useParams();
  const language = me?.language ?? 'EN';

  const { data: resolved } = api.courseBySlug.useQuery({
    path: { slug: courseSlug },
    query: { language },
  });

  const courseId = resolved?.id;

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: lessonsData } = api.courseLessons.useQuery(
    { path: { course_id: courseId ?? 0 }, query: { limit: 50 } },
    { enabled: courseId != null },
  );

  /**
   * Every per-viewer value on this page comes from here. Enrolment means
   * `access_granted_at` is set, as in legacy. Every non-student viewer gets
   * `has_access` true and no locks (preview mode).
   */
  const { data: viewerState } = api.courseViewerState.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { mutateAsync: unlockCourse, isPending: isUnlocking } =
    api.unlockCourse.useMutation();

  const hasAccess = viewerState?.has_access ?? false;
  const isComplete = viewerState?.completed_at != null;
  const progressPct = viewerState?.percent_complete ?? 0;
  const prerequisite = viewerState?.prerequisite ?? null;
  const lockedLessonIds = new Set(viewerState?.locked_lesson_ids ?? []);

  /**
   * The certificate notice's download target comes from the server:
   * `download_url` is a root-relative path to the PDF. Only fetched once the
   * state read says an award exists. A course with no template returns an empty
   * page, not every certificate in the viewer's scope.
   */
  const { data: certificates } = api.listCertificates.useQuery(
    { query: { course_id: courseId ?? 0, limit: 1 } },
    {
      enabled: courseId != null && viewerState?.certificate_awarded === true,
    },
  );
  const certificateUrl = certificates?.data[0]?.download_url ?? null;

  const lessons = lessonsData?.data ?? [];
  const lessonsCount = lessonsData?.count ?? 0;
  const topicsCount = lessons.reduce((sum, l) => sum + l.topics_count, 0);

  const mappedLessons = lessons.map((lesson) => ({
    id: lesson.id,
    title: lesson.title,
    slug: lesson.slug,
    topicsCount: lesson.topics_count,
    // An affordance, not a gate: nothing server-side refuses a locked lesson.
    // Legacy shows the same lock glyph.
    isLocked: lockedLessonIds.has(lesson.id),
  }));

  const allExpanded = mappedLessons.every((_, i) => expandedSections[i]);

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      setExpandedSections({});
    } else {
      const all: Record<number, boolean> = {};
      mappedLessons.forEach((_, i) => {
        all[i] = true;
      });
      setExpandedSections(all);
    }
  };

  const handleUnlock = async () => {
    if (courseId == null) return;
    await unlockCourse({ path: { course_id: courseId } });
    // The unlock sets `access_granted_at` and creates the progress row, so both
    // reads behind this page are stale. The response carries the new state, but
    // the lesson rows read `course_progress` separately.
    await Promise.all([
      api.courseViewerState.invalidateQueries(),
      api.courseProgress.invalidateQueries(),
    ]);
  };

  // Legacy's lower-cased "course" label, interpolated twice.
  const courseLabel = t('lessons.course').toLowerCase();

  const certificateHref =
    viewerState?.certificate_awarded === true && certificateUrl !== null
      ? `${API_ENDPOINT}${certificateUrl}`
      : null;

  // Legacy's notice is for a viewer blocked by the prerequisite; an enrolled
  // student has already met it, and preview mode bypasses it.
  // `CourseViewerState` carries the prerequisite course but not whether the
  // student finished it, so the notice is only certainly true when not
  // enrolled.
  const prerequisiteNotice = isStudent && !hasAccess ? prerequisite : null;

  return (
    <Wrapper>
      <CourseHero
        title={courseDetail?.title ?? ''}
        imageUrl={courseDetail?.cover_image || undefined}
      />
      <div style={{ overflow: 'visible', padding: '0 20px' }}>
        <ContentWrapper>
          <MainContent>
            {isStudent &&
              (certificateHref !== null ||
                hasAccess ||
                prerequisiteNotice !== null) && (
                <NoticeWrapper>
                  {/* Outside the `!isComplete` branch: an award exists only
                     for a finished course, so it could never render there. */}
                  {certificateHref !== null && (
                    <CertificateNotice downloadUrl={certificateHref} />
                  )}
                  {hasAccess && (
                    <CourseProgressIndicator
                      progress={progressPct}
                      lastActivityDate={viewerState?.last_activity_at ?? null}
                      stepsCompleted={viewerState?.topics_completed ?? 0}
                      stepsTotal={viewerState?.topics_total ?? 0}
                      isComplete={isComplete}
                    />
                  )}
                  {prerequisiteNotice !== null && (
                    <NoticeBox icon={faExclamationCircle} bold>
                      {formatString(
                        t('wbs.to-take-this-1'),
                        courseLabel,
                        courseLabel,
                      )}{' '}
                      <Link
                        to="/courses/$courseSlug/"
                        params={{ courseSlug: prerequisiteNotice.slug }}
                      >
                        {prerequisiteNotice.title}
                      </Link>
                    </NoticeBox>
                  )}
                </NoticeWrapper>
              )}
            <CourseDescription
              // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from trusted CMS
              dangerouslySetInnerHTML={{ __html: courseDetail?.content ?? '' }}
            />

            <CourseContent
              courseId={courseId || 0}
              lessons={mappedLessons}
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
              onNavigateToLesson={(lessonSlug) => {
                navigate({
                  to: '/lessons/$lessonSlug/',
                  params: { lessonSlug },
                });
              }}
              onExpandCollapseAll={handleExpandCollapseAll}
              allExpanded={allExpanded}
            />
          </MainContent>

          <CourseInfoSidebar
            imageUrl={courseDetail?.thumbnail ?? ''}
            lessonsCount={lessonsCount}
            topicsCount={topicsCount}
            hasCertificate={courseDetail?.has_certificate ?? false}
            requiresStudyHelper={courseDetail?.study_helper_required ?? false}
            requiredSeries={courseDetail?.required_series ?? []}
            progress={progressPct}
            hasAccess={hasAccess}
            unlockable={viewerState?.unlockable ?? false}
            onlineForbidden={viewerState?.online_forbidden ?? false}
            onlineForbiddenMessage={viewerState?.online_forbidden_message ?? ''}
            onUnlock={handleUnlock}
            isUnlocking={isUnlocking}
          />
        </ContentWrapper>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  background: var(--grey-150);
  min-height: 100vh;
`;

const NoticeWrapper = styled.div`
  margin-bottom: 51px;
`;

const ContentWrapper = styled.div`
  display: grid;
  gap: 40px;
  grid-template-columns: 1fr 360px;
  margin: 0 auto;
  max-width: 1200px;
  padding: 50px 0px 83px 0px;
  position: relative;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CourseDescription = styled.div`
  color: var(--grey-900);
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 58px 0;
`;
